"use client";

import { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import posthog from "posthog-js";

const SHOW_AFTER_MS = 40_000; // 40 seconds on the page
const SHOW_AFTER_SCROLL_PCT = 0.5; // or scrolled halfway down

/**
 * Sticky "ready for a quote?" bar that slides up after 40 seconds on a
 * service page OR after the visitor scrolls past 50% of it, whichever comes
 * first. Catches the long-dwell research pattern session recordings flagged:
 * people spending tens of minutes browsing without ever clicking "Get a
 * Quote." Dismissal is remembered per-service for the session.
 */
export default function QuoteStickyBar({
  serviceTitle,
  serviceSlug,
  orderHref,
}: {
  serviceTitle: string;
  serviceSlug: string;
  orderHref: string;
}) {
  const dismissKey = `cta_dismissed_${serviceSlug}`;
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [tracked, setTracked] = useState(false);

  // Honor prior dismissal within this browser session.
  useEffect(() => {
    try {
      if (sessionStorage.getItem(dismissKey)) setDismissed(true);
    } catch {
      // sessionStorage can throw in some privacy modes — silently ignore.
    }
  }, [dismissKey]);

  // Surface after a timer or scroll trigger — whichever happens first.
  useEffect(() => {
    if (dismissed) return;
    const timer = setTimeout(() => setVisible(true), SHOW_AFTER_MS);
    const onScroll = () => {
      const doc = document.documentElement;
      const scrolled =
        (window.scrollY + window.innerHeight) / Math.max(1, doc.scrollHeight);
      if (scrolled >= SHOW_AFTER_SCROLL_PCT) setVisible(true);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, [dismissed]);

  // Telemetry: fire once when the bar first appears so we can measure how
  // often it shows, gets clicked, and gets dismissed.
  useEffect(() => {
    if (visible && !tracked) {
      setTracked(true);
      posthog.capture("service_sticky_cta_shown", { service: serviceSlug });
    }
  }, [visible, tracked, serviceSlug]);

  const onDismiss = useCallback(() => {
    setDismissed(true);
    setVisible(false);
    try {
      sessionStorage.setItem(dismissKey, "1");
    } catch {}
    posthog.capture("service_sticky_cta_dismissed", { service: serviceSlug });
  }, [serviceSlug, dismissKey]);

  const onClick = useCallback(() => {
    posthog.capture("service_sticky_cta_clicked", { service: serviceSlug });
  }, [serviceSlug]);

  if (dismissed || !visible) return null;

  return (
    <div
      role="region"
      aria-label="Quote call to action"
      className="fixed bottom-0 left-0 right-0 z-40 bg-black/95 backdrop-blur-sm border-t border-white/10 text-white shadow-[0_-8px_24px_rgba(0,0,0,0.4)] animate-[slideUp_240ms_ease-out]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-3 sm:gap-5">
        <p className="text-sm sm:text-base text-white/90 flex-1 min-w-0">
          <span className="text-brand-orange font-semibold uppercase tracking-[0.2em] text-[10px] mr-3 hidden sm:inline">
            Ready?
          </span>
          <span className="hidden sm:inline">Get a quote on </span>
          <span className="sm:hidden">Quote for </span>
          <span className="font-semibold">{serviceTitle}</span>
          <span className="hidden sm:inline">. We respond within one business day.</span>
        </p>
        <Link
          href={orderHref}
          onClick={onClick}
          className="bg-brand-orange hover:bg-brand-orange/90 text-white text-[11px] uppercase tracking-[0.2em] font-bold px-5 sm:px-8 py-3 transition-colors whitespace-nowrap shrink-0"
        >
          Start Quote →
        </Link>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="text-white/40 hover:text-white transition-colors p-1 shrink-0"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <style jsx>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
